import React, {useContext, useState} from "react";
import {createUseStyles} from "react-jss";
import {EngageAppContext} from "../../App";
import {QuestionDefinition} from "../../state/questions";
import {Theme} from "engage-client/theme";
import DisplayText, {TextVariant} from "engage-client/components/DisplayText";
import {TriviaAction} from "../../messages/messages";
import Button from "engage-client/components/Button";
import {IssueType, loadTriviaFromFile, TriviaFileIssues, TriviaIssue} from "../../utils/trivialoader";
import clsx from "clsx";
import DoubleArrowIcon from "engage-client/components/icons/DoubleArrowIcon";
import {Virtuoso} from "react-virtuoso";

const useStyles = createUseStyles<Theme>((theme) => ({
	titleBar: {
		color: "white",
		padding: 10,
		backgroundColor: theme.color.primary.main
	},
	questionUpload: {
		height: 500,
		width: 500,
	},
	fileStatus: {
		padding: 16
	},
	issues: {
		overflowY:"auto",
	},
	buttons: {
		position: "absolute",
		bottom: 0,
		left: 0,
		right: 0,
		borderTop: "1px solid gray",
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		padding: 16
	},
	button: {
		margin: 16
	}
}));

type QuestionUploadProps = {
	closeHandler:()=>void
}

const QuestionUpload = React.forwardRef<HTMLDivElement, QuestionUploadProps>(({closeHandler}:QuestionUploadProps, ref) => {
	const classes = useStyles();
	const {sendMessage} = useContext(EngageAppContext.current);
	const [file,setFile] = useState<File>();
	const [questions, setQuestions] = useState<QuestionDefinition[]>();
	const [issues,setIssues] = useState<TriviaFileIssues>();
	const [errorsCollapsed, setErrorsCollapsed] = useState(false);
	const [warningsCollapsed, setWarningsCollapsed] = useState(true);

	function handleChange(files:FileList|null) {
		if(files?.length) {
			const file = files[0];
			loadFromFile(file);
		}
	}

	function loadFromFile(file:File) {
		loadTriviaFromFile(file).then((result) => {
			if(result.questions?.length) {
				setFile(file);
				setQuestions(result.questions);
			} else {
				setFile(undefined);
				setQuestions(undefined);
			}
			setErrorsCollapsed(result.issues[0].length === 0);
			setWarningsCollapsed(result.issues[0].length > 0);
			setIssues(result.issues);
		});
	}

	function toggleCollapsed(type:IssueType) {
		switch (type) {
			case IssueType.Error:
				setErrorsCollapsed(prev => !prev);
				setWarningsCollapsed(true);
				break;
			case IssueType.Warning:
				setWarningsCollapsed(prev => !prev);
				setErrorsCollapsed(true);
				break;
		}
	}

	function handleSubmit() {
		if(questions) {
			sendMessage({type: TriviaAction.ChangeQuestions, questions});
			closeHandler();
		}
	}

	return (
		<div className={classes.questionUpload} ref={ref}>
			<div className={classes.titleBar}>
				<DisplayText variant={TextVariant.s24reg} fontWeight={600}>Upload New Questions</DisplayText>
			</div>

			<input
				color="primary"
				accept="text/csv"
				type="file"
				onChange={(e) => {handleChange(e.target.files);}}
				id="questions-csv-input"
				style={{display: "none"}}
			/>
			<label htmlFor="questions-csv-input">
				<Button
					className={classes.button}
					component="span"
				>
					Select File
				</Button>
				{file?.name}
			</label>

			{!!issues?.[IssueType.Error].length &&
			<IssueList issues={issues[IssueType.Error]} collapsed={errorsCollapsed}
					   toggleCollapsed={()=>{toggleCollapsed(IssueType.Error);}}
					   title={"Errors"} highlightColor={"darkred"} backgroundColor={"mistyrose"}/>
			}

			{!!issues?.[IssueType.Warning].length &&
			<IssueList issues={issues[IssueType.Warning]} collapsed={warningsCollapsed}
					   toggleCollapsed={()=>{toggleCollapsed(IssueType.Warning);}}
					   title={"Warnings"} highlightColor={"#DD7C36"} backgroundColor={"#E6D9D4"}/>
			}

			<div className={classes.buttons}>
				<Button onClick={closeHandler}>Cancel</Button>
				<Button color="primary" onClick={handleSubmit}
						disabled={!file || (issues && issues[IssueType.Error].length > 0)}>
					Upload and Set
				</Button>
			</div>
		</div>
	);

});

export default QuestionUpload;


type IssueListProps = {
	issues: TriviaIssue[]
	collapsed: boolean
	toggleCollapsed:()=>void
	title: string
	highlightColor: string
	backgroundColor: string
}

const useListStyles = createUseStyles({
	issueList: {
		marginBottom: 20,
		borderBottom: props => props.collapsed ? "none" : `1px solid ${props.highlightColor}`,
	},
	topBar: {
		display: "flex",
	},
	issueNumber: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		padding: 10,
		backgroundColor: props => props.highlightColor,
	},
	title: {
		backgroundColor: props => props.backgroundColor,
		border: props => `1px dashed ${props.highlightColor}`,
		color: props => props.highlightColor,
		flex: 1,
		padding: 10,
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		cursor: "pointer",
	},
	issueRow: {
		display: "flex",
		alignItems: "center",
		height: props => props.rowHeight,
		padding: [0, 10],
		"&.highlighted": {
			backgroundColor: props => props.backgroundColor
		}
	},
	userNickname: {
		marginRight: 20,
	},
	collapseIcon: {
		transform: props => `scaleY(${props.collapsed ? -1 : 1})`,
		transition: "transform 250ms ease-in-out"
	},
	issueRowNumber:{
		width: 50
	},
	issueQuestionNumber: {
		width: 50
	},
});

function IssueList(props:IssueListProps) {
	const {issues, title, highlightColor, backgroundColor, collapsed, toggleCollapsed} = props;
	const rowHeight = 34; //originally taken from the height with current CSS.
	const classes = useListStyles({
		rowHeight,
		highlightColor,
		backgroundColor,
		collapsed
	});
	const listHeight = Math.min(34*(issues.length+1), 175);
	const header = {
		row: "Row#",
		question:"Qu.#",
		message: "Message",
		issueType: IssueType.Error
	};
	function renderRow(index:number) {
		let render:any = header;
		if(index > 0) {
			render = issues[index-1];
		}

		return (
			<div key={index} data-qa="issue-row" className={clsx(classes.issueRow, index%2 === 0 && "highlighted")}>
				<DisplayText variant={TextVariant.s14med} fontWeight={400} color="primary"
							 component="span" className={classes.issueRowNumber} data-qa="issue-row">
					{render.row}
				</DisplayText>
				<DisplayText variant={TextVariant.s14med} fontWeight={400} color="primary"
							 component="span" className={classes.issueQuestionNumber} data-qa="issue-question">
					{render.question}
				</DisplayText>
				<DisplayText variant={TextVariant.s14med} fontWeight={400} color="primary"
							 component="span" data-qa="issue-message">
					{render.message}
				</DisplayText>
			</div>
		);
	}

	return (
		<div className={classes.issueList}>
			<div className={classes.topBar}>
				<DisplayText variant={TextVariant.s18med}
							 className={classes.issueNumber}
							 color="white">
					{issues.length}
				</DisplayText>
				<div className={classes.title} onClick={()=>toggleCollapsed()}>
					<DisplayText variant={TextVariant.s18med}>
						{title}
					</DisplayText>
					<DoubleArrowIcon className={classes.collapseIcon} color={highlightColor}/>
				</div>
			</div>
			<Virtuoso style={{width:"100%", height: collapsed ? 0 : listHeight}}
					  totalCount={issues.length+1} topItems={1}
					  item={renderRow}/>
		</div>

	);
}